import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TrendUp, ArrowRight, Calculator, Info } from "@phosphor-icons/react";

function fmt(n) {
  return "$" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function CompoundCalc() {
  const [principal, setPrincipal] = useState("1000");
  const [rate, setRate] = useState("8");
  const [years, setYears] = useState("10");
  const [monthly, setMonthly] = useState("100");
  const [result, setResult] = useState(null);

  const calculate = () => {
    const P = parseFloat(principal) || 0;
    const r = (parseFloat(rate) || 0) / 100;
    const t = parseInt(years) || 0;
    const M = parseFloat(monthly) || 0;
    const n = 12;

    // Future value of lump sum
    const fvLump = P * Math.pow(1 + r / n, n * t);
    // Future value of monthly contributions (annuity)
    const fvAnnuity = M > 0 ? M * ((Math.pow(1 + r / n, n * t) - 1) / (r / n)) : 0;
    const total = fvLump + fvAnnuity;
    const totalContributed = P + M * 12 * t;
    const interestEarned = total - totalContributed;

    // Year-by-year breakdown
    const breakdown = [];
    let bal = P;
    for (let y = 1; y <= t; y++) {
      for (let m = 0; m < 12; m++) {
        bal = bal * (1 + r / n) + M;
      }
      breakdown.push({ year: y, balance: bal, contributed: P + M * 12 * y, interest: bal - (P + M * 12 * y) });
    }

    setResult({ total, totalContributed, interestEarned, breakdown });
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-5 space-y-4" data-testid="compound-calculator">
      <div className="flex items-center gap-2 mb-1">
        <Calculator size={16} weight="bold" className="text-emerald-400" />
        <h3 className="text-sm font-heading font-bold text-zinc-100">Compound Interest Calculator</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Starting Amount</label>
          <Input data-testid="calc-principal" type="number" value={principal} onChange={e => setPrincipal(e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-200 h-9 text-sm font-data" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Annual Rate (%)</label>
          <Input data-testid="calc-rate" type="number" value={rate} onChange={e => setRate(e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-200 h-9 text-sm font-data" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Years</label>
          <Input data-testid="calc-years" type="number" value={years} onChange={e => setYears(e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-200 h-9 text-sm font-data" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Monthly Add</label>
          <Input data-testid="calc-monthly" type="number" value={monthly} onChange={e => setMonthly(e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-200 h-9 text-sm font-data" />
        </div>
      </div>
      <Button data-testid="calc-btn" onClick={calculate} className="bg-emerald-700 hover:bg-emerald-600 text-zinc-50 h-9 text-xs px-5">
        Calculate
      </Button>

      {result && (
        <div className="space-y-4 pt-3 border-t border-zinc-800/50">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-zinc-950/60 border border-zinc-800/40 rounded-sm p-3 text-center">
              <p className="text-[9px] uppercase tracking-wider text-zinc-600">Total Contributed</p>
              <p className="text-base font-data font-bold text-zinc-200 mt-1" data-testid="calc-contributed">{fmt(result.totalContributed)}</p>
            </div>
            <div className="bg-zinc-950/60 border border-emerald-900/30 rounded-sm p-3 text-center">
              <p className="text-[9px] uppercase tracking-wider text-emerald-600">Interest Earned</p>
              <p className="text-base font-data font-bold text-emerald-400 mt-1" data-testid="calc-interest">{fmt(result.interestEarned)}</p>
            </div>
            <div className="bg-zinc-950/60 border border-zinc-800/40 rounded-sm p-3 text-center">
              <p className="text-[9px] uppercase tracking-wider text-zinc-600">Final Balance</p>
              <p className="text-base font-data font-bold text-zinc-100 mt-1" data-testid="calc-total">{fmt(result.total)}</p>
            </div>
          </div>

          {result.breakdown.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]" data-testid="calc-table">
                <thead>
                  <tr className="border-b border-zinc-800/60 bg-zinc-900/60">
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Year</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Contributed</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Interest</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {result.breakdown.map(row => (
                    <tr key={row.year} className="border-b border-zinc-800/30 hover:bg-zinc-900/30">
                      <td className="px-3 py-1.5 text-zinc-400 font-data">{row.year}</td>
                      <td className="px-3 py-1.5 text-zinc-300 font-data text-right">{fmt(row.contributed)}</td>
                      <td className="px-3 py-1.5 text-emerald-400 font-data text-right">{fmt(row.interest)}</td>
                      <td className="px-3 py-1.5 text-zinc-100 font-data text-right font-medium">{fmt(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const SECTIONS = [
  {
    title: "What Is Compound Interest?",
    content: "Compound interest is interest earned on both your original money AND the interest it has already earned. Instead of earning a flat amount each year, your money grows faster over time because your gains start generating their own gains.",
    highlight: "Simple interest: You earn on the original amount only. Compound interest: You earn on the original PLUS all accumulated interest.",
  },
  {
    title: "The Formula",
    content: null,
    formula: "A = P (1 + r/n)^(nt)",
    formulaParts: [
      { label: "A", desc: "Final amount" },
      { label: "P", desc: "Principal (starting amount)" },
      { label: "r", desc: "Annual interest rate (as decimal)" },
      { label: "n", desc: "Times compounded per year" },
      { label: "t", desc: "Number of years" },
    ],
  },
  {
    title: "Simple vs Compound: Side by Side",
    content: null,
    comparison: {
      scenario: "$10,000 at 8% for 20 years",
      simple: { earned: "$16,000", total: "$26,000", note: "8% x $10,000 = $800/yr x 20 = $16,000" },
      compound: { earned: "$36,610", total: "$46,610", note: "Interest earns interest, accelerating growth each year" },
    },
  },
  {
    title: "Real-World Examples",
    examples: [
      {
        name: "The Early Starter",
        detail: "Age 25: Invests $200/month at 8% for 40 years",
        result: "$698,202",
        contributed: "$96,000",
        lesson: "Starting early lets compounding do most of the work. Over 86% of the final amount is pure interest.",
      },
      {
        name: "The Late Starter",
        detail: "Age 40: Invests $500/month at 8% for 25 years",
        result: "$475,513",
        contributed: "$150,000",
        lesson: "Despite investing $54,000 MORE, starting 15 years later results in $222,000 LESS. Time beats money.",
      },
      {
        name: "The Lump Sum",
        detail: "$50,000 one-time investment at 10% for 30 years",
        result: "$872,470",
        contributed: "$50,000",
        lesson: "A single investment can grow 17x with enough time and a solid return rate.",
      },
    ],
  },
  {
    title: "The Rule of 72",
    content: "Divide 72 by your annual return rate to estimate how many years it takes to double your money.",
    highlight: null,
    rule72: [
      { rate: "4%", years: "18 years" },
      { rate: "6%", years: "12 years" },
      { rate: "8%", years: "9 years" },
      { rate: "10%", years: "7.2 years" },
      { rate: "12%", years: "6 years" },
    ],
  },
  {
    title: "Compounding Frequency Matters",
    content: "$10,000 at 10% for 10 years compounded at different frequencies:",
    freqTable: [
      { freq: "Annually (1x/yr)", result: "$25,937" },
      { freq: "Quarterly (4x/yr)", result: "$26,851" },
      { freq: "Monthly (12x/yr)", result: "$27,070" },
      { freq: "Daily (365x/yr)", result: "$27,183" },
    ],
    highlight: "More frequent compounding = more growth. Most investments compound daily or continuously.",
  },
  {
    title: "Key Takeaways",
    takeaways: [
      "Start as early as possible — time is the #1 factor",
      "Reinvest your returns — don't withdraw interest",
      "Consistency beats timing — regular contributions add up",
      "Higher rates amplify compounding dramatically",
      "Compounding works AGAINST you on debt — pay off high-interest loans fast",
    ],
  },
  {
    title: "The Hidden Edge: Delayed Gratification & Emotional Control",
    isHint: true,
    hintIntro: "Compound interest is a math formula — but the real challenge is psychological. The people who build wealth aren't smarter. They're more patient.",
    hintPoints: [
      {
        heading: "Delayed Gratification",
        points: [
          "Every dollar you spend today is $10-$30 you won't have in 20-30 years",
          "Skipping a $5 daily coffee and investing it instead = $150/month = ~$118,000 in 25 years at 8%",
          "The marshmallow test applies to money: those who wait, win disproportionately",
          "Wealth isn't built by earning more — it's built by spending less than you earn and investing the difference consistently",
        ],
      },
      {
        heading: "Controlling Emotions",
        points: [
          "Fear makes you sell at the bottom. Greed makes you buy at the top. Both destroy compounding",
          "The S&P 500 has recovered from every single crash in history. Panic sellers miss the recovery",
          "Missing just the 10 best trading days over 20 years cuts your returns by more than HALF",
          "Set it and forget it — automate your investments so emotions never touch your money",
          "The market rewards patience, not activity. Every time you react emotionally, compounding resets",
        ],
      },
      {
        heading: "The Mindset Shift",
        points: [
          "Think in decades, not days. Wealth is a slow cooker, not a microwave",
          "Boring is profitable — the best investment strategy is one you can stick with for 30 years",
          "Your future self will thank you for the sacrifice your present self makes today",
        ],
      },
    ],
  },
];

export default function CompoundInterest() {
  return (
    <div data-testid="compound-interest-page">
      <div className="bg-zinc-950 border-b border-zinc-800/60 px-5 py-4">
        <div className="flex items-center gap-2.5">
          <TrendUp size={22} weight="bold" className="text-emerald-400" />
          <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Compound Interest</h1>
          <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-emerald-900/30 text-emerald-400 h-4">LEARN</Badge>
        </div>
        <p className="text-xs text-zinc-500 mt-1">The most powerful force in finance — understand it, use it</p>
      </div>

      <div className="p-5 max-w-[900px] space-y-5">

        {/* Calculator first */}
        <CompoundCalc />

        {/* Sections */}
        {SECTIONS.map((s, i) => (
          <div key={i} className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-5 space-y-3" data-testid={`section-${i}`}>
            <h2 className="text-base font-heading font-bold text-zinc-100 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-emerald-900/30 text-emerald-400 text-[10px] font-data flex items-center justify-center font-bold">{i + 1}</span>
              {s.title}
            </h2>

            {s.content && <p className="text-sm text-zinc-400 leading-relaxed">{s.content}</p>}

            {s.highlight && (
              <div className="bg-emerald-950/20 border border-emerald-800/20 rounded-sm px-4 py-3">
                <p className="text-xs text-emerald-300 leading-relaxed">{s.highlight}</p>
              </div>
            )}

            {/* Formula */}
            {s.formula && (
              <div className="space-y-3">
                <div className="bg-zinc-950/60 border border-zinc-800/40 rounded-sm px-5 py-4 text-center">
                  <p className="text-xl font-data font-bold text-emerald-400 tracking-wide">{s.formula}</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {s.formulaParts.map(fp => (
                    <div key={fp.label} className="bg-zinc-950/40 border border-zinc-800/30 rounded-sm px-3 py-2 text-center">
                      <p className="text-sm font-data font-bold text-emerald-400">{fp.label}</p>
                      <p className="text-[10px] text-zinc-500 mt-0.5">{fp.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comparison */}
            {s.comparison && (
              <div className="space-y-2">
                <p className="text-xs text-zinc-500 font-data">{s.comparison.scenario}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-zinc-950/60 border border-zinc-800/40 rounded-sm p-4">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-2">Simple Interest</p>
                    <p className="text-lg font-data font-bold text-zinc-300">{s.comparison.simple.total}</p>
                    <p className="text-[10px] text-zinc-500 mt-1">{s.comparison.simple.note}</p>
                    <p className="text-xs text-zinc-400 mt-2">Interest: {s.comparison.simple.earned}</p>
                  </div>
                  <div className="bg-zinc-950/60 border border-emerald-900/30 rounded-sm p-4">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-600 mb-2">Compound Interest</p>
                    <p className="text-lg font-data font-bold text-emerald-400">{s.comparison.compound.total}</p>
                    <p className="text-[10px] text-zinc-500 mt-1">{s.comparison.compound.note}</p>
                    <p className="text-xs text-emerald-300 mt-2">Interest: {s.comparison.compound.earned}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Examples */}
            {s.examples && (
              <div className="space-y-3">
                {s.examples.map((ex, j) => (
                  <div key={j} className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[200px]">
                        <p className="text-sm font-heading font-bold text-zinc-200">{ex.name}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">{ex.detail}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-data font-bold text-emerald-400">{ex.result}</p>
                        <p className="text-[10px] text-zinc-600">contributed: {ex.contributed}</p>
                      </div>
                    </div>
                    <div className="mt-2 bg-emerald-950/15 border border-emerald-800/15 rounded-sm px-3 py-2">
                      <p className="text-[11px] text-emerald-300 flex items-start gap-1.5">
                        <ArrowRight size={11} weight="bold" className="mt-0.5 shrink-0" />
                        {ex.lesson}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Rule of 72 */}
            {s.rule72 && (
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-zinc-800/60 bg-zinc-900/60">
                      <th className="text-left px-3 py-2 text-zinc-500 font-medium">Annual Return</th>
                      <th className="text-right px-3 py-2 text-zinc-500 font-medium">Years to Double</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.rule72.map(r => (
                      <tr key={r.rate} className="border-b border-zinc-800/30 hover:bg-zinc-900/30">
                        <td className="px-3 py-1.5 text-zinc-300 font-data">{r.rate}</td>
                        <td className="px-3 py-1.5 text-emerald-400 font-data text-right font-medium">{r.years}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Frequency table */}
            {s.freqTable && (
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-zinc-800/60 bg-zinc-900/60">
                      <th className="text-left px-3 py-2 text-zinc-500 font-medium">Frequency</th>
                      <th className="text-right px-3 py-2 text-zinc-500 font-medium">Final Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.freqTable.map(f => (
                      <tr key={f.freq} className="border-b border-zinc-800/30 hover:bg-zinc-900/30">
                        <td className="px-3 py-1.5 text-zinc-300 font-data">{f.freq}</td>
                        <td className="px-3 py-1.5 text-emerald-400 font-data text-right font-medium">{f.result}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Takeaways */}
            {s.takeaways && (
              <ul className="space-y-2">
                {s.takeaways.map((t, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-sm text-zinc-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            )}

            {/* Hint: Delayed Gratification & Emotional Control */}
            {s.isHint && (
              <div className="space-y-4">
                <div className="bg-amber-950/15 border border-amber-800/25 rounded-sm px-4 py-3">
                  <p className="text-[10px] uppercase tracking-wider text-amber-500 font-bold mb-1.5 flex items-center gap-1.5">
                    <Info size={12} weight="bold" /> Hint
                  </p>
                  <p className="text-sm text-amber-200/80 leading-relaxed">{s.hintIntro}</p>
                </div>
                {s.hintPoints.map((group, gi) => (
                  <div key={gi} className="bg-zinc-950/50 border border-zinc-800/40 rounded-sm p-4 space-y-2">
                    <h4 className="text-sm font-heading font-bold text-zinc-200">{group.heading}</h4>
                    <ul className="space-y-1.5">
                      {group.points.map((pt, pi) => (
                        <li key={pi} className="flex items-start gap-2.5 text-[13px] text-zinc-400 leading-relaxed">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500/70 mt-1.5 shrink-0" />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
